import {
    Box,
    Button,
    FormHelperText,
    Grid,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { camelCaseToLowerCase } from '../../../../shared/utils';
import { FormValues, Space } from '../../../types/experience';
import IndividualSpace, { ModifiedSpace } from './IndividualSpace';
import SpaceModal from './SpaceModal';
import { sortSpacesByType } from '../IndividualExperienceSpacesModal';
import { useGetSpaceBookingsCountMutation } from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import { useFormState } from '../Form/useFormState';

export const generateSpaceNames = (spaces: Space[]): ModifiedSpace[] => {
    const typeCounts: Record<string, number> = {};

    const spacesWithNames = spaces.map((space) => {
        const type = space.spaceType;
        typeCounts[type] = (typeCounts[type] || 0) + 1;
        const titleCaseType = camelCaseToLowerCase(type);

        const name = `${titleCaseType} ${typeCounts[type]}`;
        return { ...space, name };
    });

    return spacesWithNames;
};

export default function SpaceConfiguration() {
    const { experience } = useFormState();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();

    const {
        control,
        formState: { errors, submitCount },
    } = useFormContext<FormValues>();
    const spacesErrorRef = useRef<HTMLDivElement>(null);

    const {
        fields: spaces,
        append,
        remove,
        update,
    } = useFieldArray({
        name: 'spaces',
        control,
        keyName: '_id',
    });

    const [getBookingCount] = useGetSpaceBookingsCountMutation();
    const [showModal, setShowModal] = useState(false);

    const handleAddSpace = (newSpace: Space) => {
        const modifiedSpace = {
            ...newSpace,
            cleaningFee: 0,
            newSpace: true, // So that we only apply the animation to new spaces
            demo_id: Date.now(),
        };

        append(modifiedSpace);
        setShowModal(false);
    };

    const handleUpdate = (index: number, updatedSpace: Space) => {
        const modifiedSpace = {
            ...updatedSpace,
            newSpace: false,
        };

        update(index, modifiedSpace);
    };

    const handleDelete = async (index: number, space: ModifiedSpace) => {
        try {
            if (space.id) {
                const response = await getBookingCount([
                    loggedInUser?.company,
                    space.id,
                ]).unwrap();

                if (response.bookingsCount > 0) {
                    toast.error(
                        'Failed to delete as there are existing bookings for this space. You can make this space unavailable for future bookings instead.',
                    );
                    return;
                }
            }

            remove(index);
        } catch (_error) {
            toast.error('Error fetching space data');
        }
    };
    const duplicate = (space: ModifiedSpace) => {
        const { id, ...rest } = space;
        const modifiedSpace = {
            ...rest,
            newSpace: true,
        };
        append(modifiedSpace);
        setShowModal(false);
    };

    useEffect(() => {
        if (!errors.spaces) return;

        const frame = requestAnimationFrame(() => {
            spacesErrorRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        });

        return () => cancelAnimationFrame(frame);
    }, [errors.spaces, submitCount]);

    return (
        <fieldset>
            <div>
                <Typography variant='h2' component='legend'>
                    Space Configuration
                </Typography>
                <Typography color='text.secondary' gutterBottom>
                    Configure the spaces available in your experience.
                </Typography>
            </div>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant='outlined'
                    onClick={() => setShowModal(true)}
                    fullWidth={isMobile}
                >
                    Add Configuration
                </Button>
            </Box>

            <SpaceModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={(space) => handleAddSpace(space)}
                existingSpace={null}
                type={experience.type}
                subType={experience.details.subType}
            />

            <Grid container columns={{ xs: 1, sm: 2, md: 3 }} spacing={4}>
                {sortSpacesByType(
                    generateSpaceNames(spaces).map((space, index) => ({
                        ...space,
                        originalIndex: index,
                    })),
                ).map((space) => (
                    <IndividualSpace
                        key={space._id}
                        index={space.originalIndex}
                        space={space}
                        handleDelete={() =>
                            handleDelete(space.originalIndex, space)
                        }
                        handleDuplicate={() => duplicate(space)}
                        handleUpdate={(updatedSpace) =>
                            handleUpdate(space.originalIndex, updatedSpace)
                        }
                        type={experience.type}
                        subType={experience.details.subType}
                    />
                ))}
            </Grid>
            <Box ref={spacesErrorRef}>
                {errors.spaces && (
                    <FormHelperText error>
                        {errors.spaces.message}
                    </FormHelperText>
                )}
            </Box>
        </fieldset>
    );
}
